import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import { AccessRequestModel } from '../../../../models/dtos/Classes/AccessRequestModel';
import { CollaborationApiService } from '../../../../services/api/collaboration-api.service';
import { DiscardCollabRequest, PublishRequest, CollaborationState } from '../../../../models/dtos';
import { EstimateSaveService } from '../../../../services/estimate-save.service';
import { CommonDataService } from '../../../../services/common-data.service';
import { IAccessRequestsDialogData } from '../../../../models/collaboration/IAccessRequestsDialogData';
import { IPublishCollabDialogData } from '../../../../models/collaboration/IPublishCollabDialogData';
import { firstValueFrom } from 'rxjs';
import { CollaborationOperationsService } from '../../../../services/common/collaboration-operations.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { CONSTANTS } from '../../../../Constants/Constants';
import { AccessRequestDeclineDialog } from '../access-request-decline-dialog/access-request-decline-dialog';
import { PublishCollabDataDialogComponent } from '../publish-collab-data-dialog/publish-collab-data-dialog.component';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';

@Component({
  selector: 'app-access-requests-dialog',
  imports: [
    CommonModule,
    MatButtonModule,
    MatTableModule,
    MatDialogModule,
    TranslatePipe,
  ],
  templateUrl: './access-requests-dialog.component.html',
  styleUrl: './access-requests-dialog.component.scss',
})
export class AccessRequestsDialogComponent {
  readonly displayedColumns: string[] = ['requestorName', 'requestMessage'];
  dataSource = new MatTableDataSource<AccessRequestModel>();

  constructor(
    public dialogRef: MatDialogRef<AccessRequestsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAccessRequestsDialogData,
    private readonly collaborationApiService: CollaborationApiService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly commonDataService: CommonDataService,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly userProfileService: UserProfileService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly dialog: MatDialog
  ) {
    this.dialogRef.disableClose = true;
    this.dataSource.data = data?.accessRequests ?? [];
  }

  async onAcceptDiscard(): Promise<void> {
    const request: DiscardCollabRequest = {
      isEstimateClosing: false,
      openEstimateRequest: {
        userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
        estimateId: this.commonDataService.getEstimateId(),
        userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
        culture: this.commonDataService.getCurrentApplicationCulture(),
        isArchive: this.commonDataService.isArchivedEstimate(),
      }
    };

    try{
      await firstValueFrom(this.collaborationApiService.acceptAccessRequestAndDiscard(request, true));
      this.commonDataService.setCollaborationState(CollaborationState.Open);
      this.dialogRef.close({ action: 'acceptDiscard' });
    }catch(error){
      if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
        return;
      }
      console.error('Error accepting and discarding access request:', error);
    }
  }

    async onAcceptPublish(): Promise<void> {
    const estimateSaveRequest = await this.estimateSaveService.createSaveEstimateRequest();
    const dialogData: IPublishCollabDialogData = {
      estimateSaveRequest,
      collaborators: this.commonDataService.getCollaboratorData(),
      isAcceptAccessRequestPublish: true
    };

    const publishDialogRef = this.dialog.open(PublishCollabDataDialogComponent, {
      width: CONSTANTS.COLLABORATION_POPUP_WIDTH,
      disableClose: true,
      data: dialogData
    });

    publishDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Child publish dialog completed successfully, close the parent AccessRequestsDialog
        this.dialogRef.close({ action: 'acceptPublish' });
      }
    });
  }

  onDecline(): void {
   const declineDialogData = {
  requestorEmails: this.data.accessRequests.map(r => r.requestorEmail),
    };

    const declineDialogRef = this.dialog.open(AccessRequestDeclineDialog, {
      data: declineDialogData,
      width: '600px',
      height:'217px',
      disableClose: true,
    });

    declineDialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'declined') {
        this.dialogRef.close({ action: 'decline' });
      }
    });
  }

  closeElement(): void {
    this.dialogRef.close();
  }
}
